import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ChevronRight, LineChart, Sparkles, Info } from 'lucide-react';
import useMarketsSummary from '../hooks/useMarketsSummary';
import { freshnessLabel, freshnessTone } from '../lib/marketFreshness';

// Ordre d'affichage imposé (par importance économique mondiale, choix éditorial)
const DISPLAY_ORDER = [
  'bitcoin', 'cac40', 'gold', 'eurusd', 'brent', 'jse',
  'ngx', 'brvm_composite', 'egx', 'cacao', 'fcfa_eur',
];

// Libellés courts + formateurs par indicateur
const META = {
  bitcoin:        { short: 'Bitcoin',     fmt: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k $` : `${v.toFixed(0)} $` },
  cac40:          { short: 'CAC 40',      fmt: (v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) },
  gold:           { short: 'Or',          fmt: (v) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} $` },
  eurusd:         { short: 'EUR/USD',     fmt: (v) => v.toFixed(4) },
  brent:          { short: 'Brent',       fmt: (v) => `${v.toFixed(1)} $` },
  jse:            { short: 'JSE',         fmt: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0) },
  ngx:            { short: 'NGX',         fmt: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0) },
  brvm_composite: { short: 'BRVM',        fmt: (v) => v.toFixed(0) },
  egx:            { short: 'EGX 30',      fmt: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0) },
  cacao:          { short: 'Cacao',       fmt: (v) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} $` },
  fcfa_eur:       { short: 'FCFA/EUR',    fmt: (v) => `${v.toFixed(2)}` },
};

function MiniSpark({ points, up }) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const width = 56;
  const height = 20;
  const values = points.map((p) => (typeof p === 'object' ? p.v : p));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  const d = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const color = up ? '#16a34a' : '#dc2626';
  return (
    <svg width={width} height={height} className="flex-shrink-0" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Fraîcheur PAR tuile : lit le status/âge propre de l'indicateur (gelé si la
// source tombe), jamais le seul updated_at global. En direct / Différé il y a X /
// Indisponible. Le libellé complet est dans l'attribut title (compact sur mobile).
function TileFreshness({ ind }) {
  if ((ind.source || 'live') === 'mock') {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase text-muted-foreground bg-muted px-1 py-0.5 rounded"
        title="Source indisponible, valeur non affichée comme réelle"
      >
        <Info className="h-2.5 w-2.5" />
        n/d
      </span>
    );
  }
  const tone = freshnessTone(ind);
  const label = freshnessLabel(ind);
  const map = {
    live:        { cls: 'text-green-600 dark:text-green-500',   dot: 'bg-green-500',  text: 'direct' },
    differe:     { cls: 'text-orange-600 dark:text-orange-400', dot: 'bg-orange-400', text: label },
    indisponible:{ cls: 'text-rose-600 dark:text-rose-400',     dot: 'bg-rose-400',   text: 'indispo.' },
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-semibold ${map.cls}`} title={label}>
      <span className={`h-1 w-1 rounded-full ${map.dot}`} />
      {map.text}
    </span>
  );
}

function Tile({ ind }) {
  const m = META[ind.key] || { short: ind.name, fmt: (v) => String(v) };
  const change = Number(ind.change_percent ?? 0);
  const up = change >= 0;
  const TrendIcon = up ? TrendingUp : TrendingDown;
  const changeColor = up ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500';

  return (
    <Link
      to="/marches"
      className="flex-shrink-0 min-w-[148px] sm:min-w-0 bg-card border border-border rounded-xl px-3 py-2.5 hover:border-[#D62828] hover:shadow-sm transition-all"
      aria-label={`${m.short} ${m.fmt(Number(ind.value))}, variation ${change.toFixed(2)} pourcent`}
    >
      <div className="flex items-center justify-between mb-1 gap-1">
        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide truncate">{m.short}</span>
        <MiniSpark points={ind.sparkline_24h} up={up} />
      </div>
      <div className="flex items-end justify-between gap-2">
        <span className="font-serif font-bold text-base leading-tight">{m.fmt(Number(ind.value))}</span>
        <span className={`flex items-center gap-0.5 text-xs font-semibold ${changeColor}`}>
          <TrendIcon className="h-3 w-3" />
          {up ? '+' : ''}{change.toFixed(2)}%
        </span>
      </div>
      <div className="mt-1 flex items-center justify-end">
        <TileFreshness ind={ind} />
      </div>
    </Link>
  );
}

export default function MarketsPulseWidget() {
  const { indicators, loading } = useMarketsSummary();

  // Ordre imposé : on ignore les indicateurs hors liste (brvm_30, sonatel)
  const byKey = new Map(indicators.map((i) => [i.key, i]));
  const ordered = DISPLAY_ORDER.map((k) => byKey.get(k)).filter(Boolean);

  // Live honnête : vrai seulement si CHAQUE tuile affichée est réellement fraîche
  // (status propre de l'item), pas le seul updated_at global.
  const liveOk = ordered.length > 0 && ordered.every((i) => freshnessTone(i) === 'live');

  return (
    <section className="container mx-auto px-4 py-6" aria-labelledby="markets-widget-title">
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <LineChart className="h-4 w-4 text-[#D62828] flex-shrink-0" />
            <h2 id="markets-widget-title" className="font-serif font-bold text-base">Marchés</h2>
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
              <span className={`h-1.5 w-1.5 rounded-full ${liveOk ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'}`} />
              <span className={liveOk ? 'text-green-700 dark:text-green-500' : 'text-muted-foreground'}>
                {loading ? '…' : liveOk ? 'Live' : 'Cache'}
              </span>
            </span>
          </div>
          <Link
            to="/marches"
            className="flex items-center gap-1 text-[#D62828] hover:underline text-[11px] font-bold min-h-[32px] flex-shrink-0"
          >
            Voir tout
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Tiles — ticker mobile / grid desktop */}
        <div className="px-3 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide sm:grid sm:grid-cols-4 md:grid-cols-6 sm:gap-3 sm:overflow-visible">
            {ordered.map((ind) => (
              <Tile key={ind.key} ind={ind} />
            ))}
          </div>
        </div>

        {/* Teaser éditorial */}
        <Link
          to="/marches"
          className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/30 hover:bg-muted/60 transition-colors group"
          aria-label="Accéder à Market Pulse, la lecture éditoriale des marchés"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-[#D62828]/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-[#D62828]" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">Décrypter les marchés</div>
              <div className="text-[11px] text-muted-foreground truncate">
                Market Pulse · sentiment, volatilité, secteurs
              </div>
            </div>
          </div>
          <span className="flex-shrink-0 text-[11px] font-bold text-[#D62828] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
            Accéder
            <ChevronRight className="h-3 w-3" />
          </span>
        </Link>
      </div>
    </section>
  );
}
